import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

export interface Me {
  id: string;
  name: string;
  accountName: string;
  email: string;
  assistantAllowed: boolean;
  settings: {
    displayName?: string;
    assistantGoal?: string;
    assistantNotes?: string;
  };
}

interface AccountState {
  me: Me | null;
  loading: boolean;
  signInAvailable: boolean;
  signIn: (returnPath?: string) => void;
  signOut: () => Promise<void>;
  setMe: (me: Me | null) => void;
}

const AccountContext = createContext<AccountState>({
  me: null,
  loading: true,
  signInAvailable: false,
  signIn: () => undefined,
  signOut: async () => undefined,
  setMe: () => undefined,
});

/** Who is signed in. Accounts are optional: everything works without one. */
export function AccountProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [signInAvailable, setSignInAvailable] = useState(false);

  useEffect(() => {
    fetch('/api/me', { headers: { accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : { user: null, signInAvailable: false }))
      .then((data) => {
        setMe(data.user ?? null);
        setSignInAvailable(!!data.signInAvailable);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  // Through the shared login (Google, passkey or email code), then back to this page.
  const signIn = useCallback((returnPath?: string) => {
    const back = returnPath ?? location.pathname + location.search;
    location.href = `/auth/login?return=${encodeURIComponent(back)}`;
  }, []);

  const signOut = useCallback(async () => {
    await fetch('/auth/logout', { method: 'POST' }).catch(() => undefined);
    setMe(null);
  }, []);

  return (
    <AccountContext value={{ me, loading, signInAvailable, signIn, signOut, setMe }}>{children}</AccountContext>
  );
}

export const useAccount = () => useContext(AccountContext);
